/**
 * Application bootstrap
 *
 * What this module does
 * - Mounts the React application into the `#root` element
 * - Initializes viewport + safe-area helpers (keyboard/URL bar aware)
 * - Registers the Firebase Cloud Messaging service worker (web only)
 *
 * Maintenance guidelines
 * - If you change the root element id in `index.html`, update the selector below.
 * - No native status bar configuration; defaults are used.
 * - FCM service worker is registered only when service workers are supported and
 *   NOT on native mobile (WebView does not support service workers).
 * - Logging is gated by Vite's `import.meta.env.DEV` to keep production quiet.
 */

import { StrictMode } from 'react';
import { createRoot } from 'react-dom/client';
import App from './App';
import { LanguageProvider } from './providers/LanguageProvider';
import { isNative, devWarn, devLog } from './bootstrap/env';
import { setupViewportMetrics } from './bootstrap/viewport';
import { registerFCMServiceWorker } from './bootstrap/fcm';

// ----------------------------------------------------------------------------
// Utilities are now provided by src/app/bootstrap/env.ts
// ----------------------------------------------------------------------------

// ----------------------------------------------------------------------------
// 0) Platform classes (before mount)
// ----------------------------------------------------------------------------

// No Android-specific platform classes; use default styling.

// ----------------------------------------------------------------------------
// 1) Mount React
// ----------------------------------------------------------------------------

/**
 * Safely obtain the root DOM element. Throws a clear error if missing so the
 * failure is surfaced early during development.
 */
function getRootElementOrThrow(): HTMLElement {
  const el = document.getElementById('root');
  if (!el) throw new Error('Failed to find the root element with id="root"');
  return el;
}

const root = createRoot(getRootElementOrThrow());

(async () => {
  // Set insets globally
  try {
    const { EdgeToEdge } = await import('@capawesome/capacitor-android-edge-to-edge-support');
    await EdgeToEdge.enable();
    const insets = await EdgeToEdge.getInsets();
    devLog('[EdgeToEdge] Insets:', insets);
    // Set bottom inset
    document.documentElement.style.setProperty('--inset-bottom', '135px');
    // Note: Bar colors will be set by useNavigationBarTheming hook based on current theme
    devLog('[Insets] Set --inset-bottom to 135px');
  } catch (error) {
    devWarn('[EdgeToEdge] Failed:', error);
    // Fallback
    document.documentElement.style.setProperty('--inset-bottom', '135px');
  }

  root.render(
    <StrictMode>
      <LanguageProvider>
        <App />
      </LanguageProvider>
    </StrictMode>
  );
})();

// ----------------------------------------------------------------------------
// 1b) Viewport + safe-area feature detection
// ----------------------------------------------------------------------------

const cleanupViewport = setupViewportMetrics();
// Ensure cleanup runs when navigating away or closing the page/tab.
window.addEventListener('pagehide', cleanupViewport, { once: true } as any);

// No Android-specific StatusBar configuration; rely on defaults.

// ----------------------------------------------------------------------------
// 3) Register service worker for Firebase Cloud Messaging (Web only)
// ----------------------------------------------------------------------------

if ('serviceWorker' in navigator) {
  // Avoid SW registration on native platforms (Android/iOS WebViews do not support SW).
  if (!isNative) registerFCMServiceWorker();
} else {
  devWarn('[FCM] Service workers not supported in this browser');
}
